import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Loader2, Plus, Server } from 'lucide-react';
import { Link, useNavigate } from 'react-router';

import { useNotification } from '@/components/notifications';
import { PageDescription } from '@/components/page-description';
import { PageTitle } from '@/components/page-title';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/copy-button';
import { ROUTES } from '@/constants/routes';
import { AuthContext } from '@/context/auth-context';
import {
  listMcpServers,
  McpServer,
  provisionGenericMcpServer,
} from '@/services/mcp-servers';
import { useOrgStore } from '@/stores/org-store';
import { isOrgAdmin } from '@/utils/org-role';
import { orgPath } from '@/utils/org-path';

import { CreateMcpServerWizard } from './create-mcp-server-wizard';

const statusVariant = (status: string) =>
  status === 'active' ? 'secondary' : 'destructive';

const MAX_LISTED_TOOLS = 6;

const toolSummary = (server: McpServer) => {
  const names = (server.tools ?? []).map((tool) => tool.tool_name);
  if (names.length === 0) return null;
  const shown = names.slice(0, MAX_LISTED_TOOLS).join(', ');
  return names.length > MAX_LISTED_TOOLS
    ? `${shown} +${names.length - MAX_LISTED_TOOLS} more`
    : shown;
};

export const McpServersPage = () => {
  const { currentOrg } = useOrgStore();
  const { user } = useContext(AuthContext);
  const canManage = isOrgAdmin(user, currentOrg);
  const navigate = useNavigate();
  const notification = useNotification();
  const notificationRef = useRef(notification);
  notificationRef.current = notification;
  const [servers, setServers] = useState<McpServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = useCallback(async () => {
    if (!currentOrg?.orgid) return;
    setLoading(true);
    try {
      const response = await listMcpServers(currentOrg.orgid);
      if (response.detail) {
        notificationRef.current.error(response.detail);
        return;
      }
      let list = response.data?.servers || [];
      // The generic org gateway is auto-provisioned: one per org, created
      // the first time an admin opens this page (idempotent server-side).
      if (
        canManage &&
        !list.some((server) => server.server_type === 'generic')
      ) {
        const provisioned = await provisionGenericMcpServer(currentOrg.orgid);
        if (provisioned.data?.server) {
          list = [provisioned.data.server, ...list];
        } else if (provisioned.detail) {
          notificationRef.current.error(
            `Org gateway provisioning failed: ${provisioned.detail}`,
          );
        }
      }
      setServers(list);
    } finally {
      setLoading(false);
    }
  }, [currentOrg?.orgid, canManage]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className='w-full'>
      <div className='mb-6 flex flex-wrap items-start justify-between gap-3'>
        <div>
          <PageTitle title='MCP Servers' />
          <PageDescription>
            MCP endpoints exposing this organization&apos;s workflows, agents,
            and data assets to external tools (Claude, Cursor, Snowflake,
            Databricks): the auto-provisioned org gateway (everything, via
            meta-tools) plus curated servers. Every member can see them; org
            admins create servers and manage their keys, docs and audit ledger.
          </PageDescription>
        </div>
        {canManage && (
          <Button onClick={() => setWizardOpen(true)} data-testid='mcp-create'>
            <Plus className='size-4' /> New MCP server
          </Button>
        )}
      </div>

      <Card className='mb-6' data-testid='mcp-personal-connections-pointer'>
        <CardContent className='flex flex-wrap items-center justify-between gap-3 pt-6 text-sm'>
          <p className='text-muted-foreground'>
            Members connect their own accounts in Connectors. Each connection
            acts only as that member; this page is for the organization&apos;s
            own MCP servers.
          </p>
          <Button asChild variant='outline' size='sm'>
            <Link to={orgPath(ROUTES.ACCOUNT.CONNECTORS)}>Open Connectors</Link>
          </Button>
        </CardContent>
      </Card>

      {loading ? (
        <div className='flex items-center gap-2 py-12 text-muted-foreground'>
          <Loader2 className='size-5 animate-spin' /> Loading MCP servers…
        </div>
      ) : servers.length === 0 ? (
        <Card>
          <CardContent className='flex flex-col items-center gap-3 py-12 text-center text-muted-foreground'>
            <Server className='size-8' />
            <p data-testid='mcp-empty'>
              {canManage ? (
                <>
                  No MCP servers yet. Create one to expose selected workflows
                  and data to external MCP clients — or ask the platform chat to
                  &ldquo;make me an MCP server&rdquo;.
                </>
              ) : (
                'No MCP servers yet. An org admin can create one.'
              )}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className='flex flex-col gap-3'>
          {!canManage && (
            <p
              className='text-sm text-muted-foreground'
              data-testid='mcp-member-note'
            >
              Need a key for one of these servers? Ask an org admin.
            </p>
          )}
          {servers.map((server) => (
            <Card
              key={server.server_id}
              className={
                canManage
                  ? 'cursor-pointer transition-colors hover:bg-muted/40'
                  : undefined
              }
              data-testid={`mcp-server-${server.slug}`}
              onClick={
                canManage
                  ? () =>
                      navigate(
                        orgPath(`/settings/mcp-servers/${server.server_id}`),
                      )
                  : undefined
              }
            >
              <CardContent className='flex flex-col gap-2 pt-6'>
                <div className='flex flex-wrap items-center gap-2'>
                  <h2 className='font-medium'>{server.name}</h2>
                  <Badge variant={statusVariant(server.status)}>
                    {server.status}
                  </Badge>
                  {server.server_type === 'generic' ? (
                    <Badge variant='outline'>
                      org gateway · auto-provisioned
                    </Badge>
                  ) : (
                    <Badge variant='outline'>{server.server_type}</Badge>
                  )}
                  {server.origin === 'chat' && (
                    <Badge variant='outline'>created via chat</Badge>
                  )}
                  <span className='ml-auto text-sm text-muted-foreground'>
                    {server.server_type === 'generic'
                      ? 'meta-tools'
                      : `${server.tool_count} tool${server.tool_count === 1 ? '' : 's'}`}
                  </span>
                </div>
                {server.server_type === 'generic' && (
                  <p className='text-xs text-amber-600 dark:text-amber-500'>
                    Org-scoped service identity — per-user scoping coming with
                    ENG-5281.
                  </p>
                )}
                {server.description && (
                  <p className='text-sm text-muted-foreground'>
                    {server.description}
                  </p>
                )}
                {toolSummary(server) && (
                  <p
                    className='text-xs text-muted-foreground'
                    data-testid={`mcp-server-tools-${server.slug}`}
                  >
                    Tools: {toolSummary(server)}
                  </p>
                )}
                <div
                  className='flex items-center gap-1 text-xs text-muted-foreground'
                  onClick={(event) => event.stopPropagation()}
                >
                  <code className='rounded bg-muted px-1.5 py-0.5'>
                    {server.endpoint}
                  </code>
                  <CopyButton text={server.endpoint} size='small' />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {canManage && (
        <CreateMcpServerWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          orgId={currentOrg?.orgid || ''}
          onCreated={load}
        />
      )}
    </div>
  );
};
